import React, { useEffect, useState } from 'react';
import { Space } from 'antd';
import ReviewForm from './Review';
import { MOCK_SUMMARY } from './constants';
import EmployeesReviewApi from '../../Api/EmployeesReviewApi';
import { toastError, toastSuccess } from '../Toast/ToastComponent';
import SkillMatrixApi from '../../Api/SkillMatrixApi';

const feedbackFields = [
  {
    label: 'Key Achievements This Month',
    placeholder: 'Describe your key achievements and accomplishments...',
  },
  {
    label: 'Challenges Faced',
    placeholder: 'Share any challenges or obstacles you encountered...',
  },
  {
    label: 'Suggestions / Improvements',
    placeholder:
      "Provide suggestions for improvement or areas you'd like to develop...",
  },
];

/**
 * Transform form values to API payload format
 */
function buildApiPayload(formValues, { status, skillRating, reviewId }) {
  const rating = formValues?.employee_review?.rating || [];
  const feedback = formValues?.employee_review?.feedback || [];

  const ratingArray = skillRating.map((row, index) => ({
    id: row.id,
    value: rating[index]?.value ?? 0,
  }));

  const feedbackArray = feedbackFields.map((field, index) => ({
    question: field.label,
    answer: feedback[index]?.answer ?? '',
  }));

  const payload = {
    review_id: reviewId,
    employee_review: {
      rating: ratingArray,
      feedback: feedbackArray,
    },
  };

  if (status) {
    payload.status = status;
  }

  return payload;
}

const DraftReviewComp = ({ reviewData }) => {
  const [saving, setSaving] = useState(false);
  const [skillRating, setSkillRating] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const skills = await fetchSkillRating();
      if (skills) {
        setSkillRating(skills);
      }
    };
    fetchData();
    return () => {
      setSkillRating(null);
    };
  }, []);

  async function fetchSkillRating() {
    try {
      const response = await SkillMatrixApi.getList({});
      return response?.data?.result || null;
    } catch (error) {
      console.error('Error fetching skill rating:', error);
      toastError(
        error?.response?.data?.message || 'Failed to fetch skill rating.',
      );
    }
  }

  const handleSubmit = async (values) => {
    try {
      setSaving(true);
      const payload = buildApiPayload(values, {
        skillRating,
        status: 'Manager Review',
        reviewId: reviewData?.id,
      });
      await EmployeesReviewApi.create(payload);
      toastSuccess('Review submitted successfully.');
    } catch (error) {
      console.error('Error submitting review:', error);
      toastError(error?.response?.data?.message || 'Failed to submit review.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async (values) => {
    try {
      setSaving(true);
      const payload = buildApiPayload(values, {
        skillRating,
        status: 'Draft',
        reviewId: reviewData?.id,
      });
      await EmployeesReviewApi.create(payload);
      toastSuccess('Draft saved successfully.');
    } catch (error) {
      console.error('Error saving draft:', error);
      toastError(error?.response?.data?.message || 'Failed to save draft.');
    } finally {
      setSaving(false);
    }
  };

  const userName = [reviewData?.user?.first_name, reviewData?.user?.last_name]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="container-fluid px-2 px-md-3">
      <ReviewForm
        monthLabel="Self Review (Draft)"
        readOnly={saving}
        onSubmit={handleSubmit}
        onSaveDraft={handleSaveDraft}
        value={reviewData}
      >
        <Space direction="vertical" size="middle" className="w-100">
          <ReviewForm.EmployeeInfoHeader
            employeeName={userName || '--'}
            department={reviewData?.department_name || '--'}
            manager={reviewData?.department_head_names || '--'}
            reviewMonth={`${reviewData?.month} ${reviewData?.year}`}
          />

          <ReviewForm.TimesheetSummary
            summary={{
              totalHours: reviewData?.total_hours || 0,
              projects: reviewData?.projects_worked || 0,
              leaves: reviewData?.total_leaves || 0,
              attendancePct: reviewData?.attendance_percentage || 0,
            }}
          />

          <ReviewForm.PerformanceRating
            title="Self Performance Rating"
            subtitle="Rate yourself on a scale of 1-5 stars"
            rows={reviewData?.employee_review?.rating || skillRating}
            parentKey={['employee_review', 'rating']}
          />

          <ReviewForm.Feedback
            title="Self Feedback"
            subtitle="Share achievements, challenges, and improvements for this review period."
            fields={feedbackFields}
            parentKey={['employee_review', 'feedback']}
          />

          <ReviewForm.Actions />
        </Space>
      </ReviewForm>
    </div>
  );
};

export default DraftReviewComp;
